/**
 * DashboardLayout — App shell with sidebar (brand + app switcher + nav) + header + content.
 * @layer layout
 * @package @plerity/dashboard-shared
 * @consumes SidebarNav
 *
 * CSS contract — exposes `--shell-header-height` on the layout root so child
 * routes can size full-viewport panels without hardcoding the header offset.
 * Use via Tailwind arbitrary value:
 *   <div className="h-[calc(100vh-var(--shell-header-height,3.5rem))]" />
 * The fallback (3.5rem) matches `SHELL_HEADER_HEIGHT` below; if the layout is
 * mounted, the var wins. This is the canonical fix for FE-11 (off-by-X-rem
 * shell-header offsets in route components).
 */

import { clsx } from "clsx";
import { type CSSProperties, type ReactNode, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import type { AppSwitcherEntry, SidebarBrand } from "../components/widgets/SidebarNav";
import { DEFAULT_APPS, DEFAULT_BRAND, SidebarNav } from "../components/widgets/SidebarNav";
import { useIsMobile } from "../hooks";
import type { NavSection } from "../types/dashboard";

/**
 * Shell-header height — single source of truth for the dashboard top bar.
 * Exposed as the `--shell-header-height` CSS variable on the layout root so
 * route components can compute viewport-fill heights without duplicating the
 * value (see CSS contract in module docblock).
 */
export const SHELL_HEADER_HEIGHT = "3.5rem";

export interface DashboardLayoutProps {
  /** Dashboard display name */
  title: string;
  /**
   * Shorter variant of `title` shown below the `sm` breakpoint (<640px),
   * where the header's available width (viewport minus sidebar toggle +
   * headerActions) can be as little as ~50-60px — not enough for a longer
   * app name, which truncates to e.g. "S…" (social live QA, 2026-10-04:
   * backlog shell-short-title-phones-2026-10-04-001). `title` still renders
   * at `sm:` and up, and remains the ONLY accessible name for the element —
   * `shortTitle` is marked `aria-hidden` so screen readers never see it.
   * Omit for apps whose `title` already fits (e.g. "SEO", "Data"); when
   * omitted, the header renders exactly as before (plain `{title}` text).
   */
  shortTitle?: string;
  /** Navigation sections for sidebar */
  navigation: NavSection[];
  /** Optional header-right content (e.g. theme toggle, user menu) */
  headerActions?: ReactNode;
  /** Sidebar collapse state (controlled externally via store) */
  sidebarCollapsed?: boolean;
  /** Callback to toggle sidebar */
  onToggleSidebar?: () => void;
  /** Brand/logo area — defaults to Animas Marketing brand */
  brand?: SidebarBrand | false;
  /** App switcher entries — defaults to Portal/CRM/Builder.
   *  Pass `false` to hide, or an array with one entry `.active = true`. */
  apps?: AppSwitcherEntry[] | false;
  /** Current app ID for auto-marking active in default app switcher */
  currentApp?: string;
  /** Footer content for sidebar (below nav, above collapse) */
  sidebarFooter?: ReactNode;
  /** Override main content (defaults to <Outlet />) */
  children?: ReactNode;
  className?: string;
  /**
   * Edge-to-edge content: drops the `<main>` padding and hands scrolling to
   * the route (`<main>` becomes `relative flex flex-col overflow-hidden`, so a
   * route root with `flex-1 min-h-0` fills the remaining viewport). Sidebar +
   * header stay. Used by canvas/editor routes.
   */
  fullBleed?: boolean;
  /**
   * Immersive mode: hides the sidebar, mobile drawer backdrop and header so
   * the route owns the whole viewport (implies `fullBleed`).
   * `--shell-header-height` is exposed as `0px` so viewport-fill calcs keep
   * working. The route MUST render its own way back (e.g. a "Back" link).
   */
  hideChrome?: boolean;
  /**
   * Reserve bottom space in `<main>` (`pb-24`) so a fixed bottom-right FAB
   * (chat assistant / tester notes, 56px at bottom-6) never covers the last
   * row of page content. createShell sets this whenever a floating widget is
   * enabled for the current user. Ignored for `fullBleed`/`hideChrome`.
   */
  reserveFabSpace?: boolean;
}

export function DashboardLayout({
  title,
  shortTitle,
  navigation,
  headerActions,
  sidebarCollapsed: sidebarCollapsedProp = false,
  onToggleSidebar,
  brand,
  apps,
  currentApp,
  sidebarFooter,
  children,
  className,
  fullBleed = false,
  hideChrome = false,
  reserveFabSpace = false,
}: DashboardLayoutProps) {
  const edgeToEdge = fullBleed || hideChrome;
  // With chrome hidden there is no drawer — treat it as collapsed so the
  // mobile scroll-lock / focus-trap / Esc effects below stay inert.
  const sidebarCollapsed = sidebarCollapsedProp || hideChrome;
  const location = useLocation();
  const isMobile = useIsMobile();

  // Mobile drawer Esc-to-close: when the sidebar is expanded on a narrow
  // viewport (drawer mode, see SidebarNav max-md:fixed/translate styles),
  // pressing Escape collapses it back. No-op on desktop where the sidebar
  // is a normal flex member.
  //
  // Note: matchMedia is checked inline INSIDE the handler (not via the
  // reactive `isMobile` from `useIsMobile()`) so the keydown listener does
  // NOT get re-attached every time the viewport flips across the md
  // boundary. The handler closure evaluates the predicate at event time.
  useEffect(() => {
    if (sidebarCollapsed || !onToggleSidebar) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && window.matchMedia("(max-width: 767px)").matches) {
        onToggleSidebar();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [sidebarCollapsed, onToggleSidebar]);

  // Mobile drawer auto-close on navigation: when the user taps a NavLink
  // inside the drawer, the route changes and we collapse so the user sees
  // the new page instead of the still-open drawer covering it. Desktop is
  // unaffected (`isMobile` gates the toggle).
  // biome-ignore lint/correctness/useExhaustiveDependencies: we only want to fire on pathname changes, not on every render where sidebarCollapsed/onToggleSidebar/isMobile might re-identify. The pathname-only dep is intentional.
  useEffect(() => {
    if (sidebarCollapsed || !onToggleSidebar) return;
    if (isMobile) {
      onToggleSidebar();
    }
  }, [location.pathname]);

  // Mobile drawer body scroll-lock: when the drawer is open at narrow widths,
  // prevent the page behind the backdrop from scrolling under touch. Desktop
  // is unaffected (`isMobile` gates the lock). Restored on cleanup so an
  // unmount mid-drawer doesn't leave the body frozen.
  useEffect(() => {
    if (sidebarCollapsed) return;
    if (!isMobile) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [sidebarCollapsed, isMobile]);

  // Mobile drawer focus management: when the drawer opens, move focus to the
  // first focusable element inside the sidebar so screen-reader / keyboard
  // users can navigate immediately. Trap Tab/Shift+Tab within the sidebar
  // while open so users don't accidentally interact with content behind the
  // backdrop. On close, restore focus to whatever element was focused when
  // the drawer opened (typically the hamburger button in the header).
  // Desktop is unaffected (`isMobile` gates the entire effect).
  useEffect(() => {
    if (sidebarCollapsed) return;
    if (!isMobile) return;

    const sidebar = document.querySelector<HTMLElement>('aside[aria-label="Main navigation"]');
    if (!sidebar) return;

    const previousFocus = document.activeElement as HTMLElement | null;
    // Standard focusable selector — covers all interactive elements that can
    // receive Tab focus by default, plus tabindex=0 overrides. Excludes
    // disabled and tabindex=-1 (programmatically focusable but not in tab order).
    const FOCUSABLE_SELECTOR =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    // Numeric tabindex parser — tabindex="0" returns 0, missing returns 0,
    // non-numeric returns 0. Positive integers preserved. We've already
    // excluded -1 via the selector.
    const getTabIndex = (el: HTMLElement): number => {
      const attr = el.getAttribute("tabindex");
      if (attr === null) return 0;
      const n = Number.parseInt(attr, 10);
      return Number.isNaN(n) ? 0 : n;
    };

    // Walk ancestors checking aria-hidden="true" — an element inside a
    // hidden subtree is not focusable even if it itself isn't aria-hidden.
    // Also normalizes the direct check: aria-hidden="false" should NOT be
    // treated as hidden (the original `hasAttribute` check was over-eager).
    const isAriaHidden = (el: HTMLElement): boolean => {
      let current: HTMLElement | null = el;
      while (current && current !== document.body) {
        if (current.getAttribute("aria-hidden") === "true") return true;
        current = current.parentElement;
      }
      return false;
    };

    // Visibility check covers BOTH `display: none` (offsetParent=null) and
    // `visibility: hidden` (offsetParent is non-null but element invisible).
    // The original check missed the latter.
    const isVisible = (el: HTMLElement): boolean => {
      if (el.offsetParent === null) return false;
      if (window.getComputedStyle(el).visibility === "hidden") return false;
      return true;
    };

    const getFocusable = (): HTMLElement[] => {
      const list = Array.from(sidebar.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => isVisible(el) && !isAriaHidden(el)
      );
      // Honor positive tabindex: positive values come BEFORE tabindex=0 in
      // tab order, ascending. Stable sort preserves DOM order within each
      // group. (W3C focus-order spec: positive tabindex first by value
      // ascending, then DOM order for tabindex 0 / native focusables.)
      // Sidebar uses tabindex=0 throughout TODAY, so this is a no-op until
      // a consumer adds positive tabindex — at which point we honor it.
      return list.sort((a, b) => {
        const aTab = getTabIndex(a);
        const bTab = getTabIndex(b);
        if (aTab > 0 && bTab > 0) return aTab - bTab;
        if (aTab > 0) return -1;
        if (bTab > 0) return 1;
        return 0; // both 0 — preserve DOM order via stable sort
      });
    };

    // Move initial focus to the first focusable element. Defer one frame so
    // CSS `transition-transform` doesn't fight the focus ring's render.
    // The `isConnected` guard handles a theoretical rAF race: if the drawer
    // remounted between effect setup and rAF firing, the captured `sidebar`
    // closure would point at a detached node. Cleanup cancels the rAF in
    // the normal close flow; this guard is belt-and-suspenders.
    const focusFirstFrame = requestAnimationFrame(() => {
      if (!sidebar.isConnected) return;
      const focusable = getFocusable();
      focusable[0]?.focus();
    });

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const focusable = getFocusable();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    sidebar.addEventListener("keydown", handleKeyDown);
    return () => {
      cancelAnimationFrame(focusFirstFrame);
      sidebar.removeEventListener("keydown", handleKeyDown);
      // Only restore focus if the previously-focused element is still in the
      // DOM (route changes can unmount it). Skip when current focus moved to
      // a navlink target (post-navigation) to avoid yanking the cursor back.
      if (previousFocus && document.body.contains(previousFocus) && !sidebar.contains(document.activeElement)) {
        previousFocus.focus();
      }
    };
  }, [sidebarCollapsed, isMobile]);

  // Resolve brand — default to Animas brand unless explicitly false
  const resolvedBrand = brand === false ? undefined : (brand ?? DEFAULT_BRAND);

  // Resolve apps — default to standard app switcher with currentApp marked active
  let resolvedApps: AppSwitcherEntry[] | undefined;
  if (apps === false) {
    resolvedApps = undefined;
  } else if (apps) {
    resolvedApps = apps;
  } else if (currentApp) {
    resolvedApps = DEFAULT_APPS.map((app) => ({
      ...app,
      active: app.id === currentApp,
    }));
  } else {
    // No currentApp specified — try to infer from title
    const titleLower = title.toLowerCase();
    resolvedApps = DEFAULT_APPS.map((app) => ({
      ...app,
      active: app.id === titleLower || app.label.toLowerCase() === titleLower,
    }));
  }

  return (
    <div
      className={clsx("flex h-screen overflow-hidden bg-surface-page", className)}
      // FE-11: expose shell-header height as a CSS var so deep route components
      // (e.g. capitan/Chat.tsx) can size viewport-fill panels via
      // `h-[calc(100vh-var(--shell-header-height,3.5rem))]` without duplicating
      // the magic number. The h-14 below is bound to the same constant.
      style={{ "--shell-header-height": hideChrome ? "0px" : SHELL_HEADER_HEIGHT } as CSSProperties}
      data-shell-chrome={hideChrome ? "hidden" : "visible"}
    >
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-primary-700 focus:px-4 focus:py-2 focus:text-white focus:shadow-lg"
      >
        Skip to main content
      </a>

      {/* Mobile drawer backdrop — only renders below `md` (768 px) when the
          sidebar is expanded (drawer visible). Click dismisses the drawer.
          z-index 30 sits below the sidebar's z-40 (see SidebarNav.tsx
          max-md:z-40) so the sidebar stays above the backdrop. */}
      {!sidebarCollapsed && onToggleSidebar && (
        <button
          type="button"
          onClick={onToggleSidebar}
          aria-label="Close navigation drawer"
          className="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm md:hidden"
        />
      )}

      {/* Sidebar — omitted entirely in immersive (hideChrome) mode */}
      {!hideChrome && (
        <SidebarNav
          sections={navigation}
          collapsed={sidebarCollapsed}
          onToggleCollapse={onToggleSidebar}
          brand={resolvedBrand}
          apps={resolvedApps}
          footer={sidebarFooter}
        />
      )}

      {/* Main area — min-w-0 prevents flex child from inflating past viewport width
          (FE-M1 mobile horizontal-overflow fix, 2026-04-27). Without min-w-0, a flex
          child's min-content-size is its intrinsic width, which wide inner elements
          (e.g. non-wrapping tab bars) can inflate. */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Header — h-14 = 3.5rem = SHELL_HEADER_HEIGHT
         *
         * Heading-level contract (2026-06-23):
         *   The shell header shows the APP name (e.g. "Finance") as navigation
         *   chrome. It is NOT a page-level heading — it does not describe the
         *   current route's content. Rendering it as <h1> created a dual-h1
         *   violation whenever a route component also used DashboardPageHeader
         *   (which correctly owns the page's <h1>).
         *
         *   Resolution: the app-name is rendered as a visually styled <p> inside
         *   the <header> landmark. Screen-reader users encounter the <header>
         *   landmark (role="banner") plus the <h1> inside the route content area
         *   — a clean single-h1 document outline.
         *
         *   DashboardPageHeader defaults to h1 ("as" prop default) and is the
         *   sole h1 per page. Phase 6 consumers (outreach SequenceBuilder, dgo
         *   PageHeader/Onboarding) should use DashboardPageHeader with the
         *   default `as="h1"` and NOT add a second h1 in their templates.
         */}
        {/* Chrome surfaces (header + sidebar) use bg-surface (white light /
            #1e293b dark) elevated over the bg-surface-page canvas
            (#f8fafc / #0f172a) — S1 fleet dark-mode decision 2026-07-07. */}
        {/* Title is truncate-not-wrap (min-w-0 + truncate) and actions are
            shrink-0, so at 375px a long app name ("Social Media") ellipsizes
            instead of wrapping to 2 lines and crowding headerActions
            (backlog social-sweep-shell-header-title-wrap-2026-10-02-004).

            `shortTitle` (optional): below `sm` the header can leave as little
            as ~50-60px for the title even WITHOUT wrapping, so a longer app
            name still truncates to e.g. "S…" (social live QA, 2026-10-04 —
            backlog shell-short-title-phones-2026-10-04-001). When provided,
            swap to a shorter label below `sm` via two spans:
              - the short span is `aria-hidden` (purely visual, never read)
              - the full span uses `sr-only sm:not-sr-only` — visually hidden
                below `sm` but still in the accessibility tree, so assistive
                tech always gets the FULL title regardless of viewport. (A
                `<p aria-label>` was considered instead but `aria-label` is
                not a supported ARIA prop on the implicit "paragraph" role.)
            Omitting `shortTitle` renders byte-identical to the pre-existing
            single-text-node markup. */}
        {!hideChrome && (
          <header className="flex h-14 items-center justify-between gap-3 border-b border-light bg-surface px-4 sm:px-6">
            <p className="min-w-0 truncate text-base font-semibold text-fg sm:text-lg" title={title}>
              {shortTitle ? (
                <>
                  <span className="sr-only sm:not-sr-only">{title}</span>
                  <span className="sm:hidden" aria-hidden="true">
                    {shortTitle}
                  </span>
                </>
              ) : (
                title
              )}
            </p>
            {headerActions && <div className="flex shrink-0 items-center gap-2 sm:gap-3">{headerActions}</div>}
          </header>
        )}

        {/* Content — overflow-x-hidden prevents tab bars / wide content from creating
            a horizontal scroll context that browser auto-scroll (focus-follows-active-tab)
            then scrolls, pushing page titles behind the sidebar (FE-M1, 2026-04-27).
            fullBleed/hideChrome: no padding, route owns scrolling (flex column so a
            `flex-1 min-h-0` route root fills the viewport).
            reserveFabSpace: pb-24 keeps the fixed bottom-right FAB (56px @ bottom-6)
            off the last row of content (backlog social-sweep-chat-fab-covers-content-2026-10-02-005).
            tabIndex (WCAG 2.1.1, axe scrollable-region-focusable — ai /sessions/context/mcp
            2026-10-12): the padded <main> is the page's scroll container; on routes with no
            focusable content keyboard users could not scroll it. axe 4.11 only accepts an
            element IN the tab order (tabindex="-1" still fails), so padded mode is 0. The
            full-bleed variant never scrolls (route owns scrolling) → -1 keeps it a skip-link
            focus target without adding a tab stop. Ring only on :focus-visible (keyboard). */}
        <main
          id="main-content"
          tabIndex={edgeToEdge ? -1 : 0}
          data-layout={edgeToEdge ? "full-bleed" : "padded"}
          className={clsx(
            "flex-1 bg-surface-page focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500",
            edgeToEdge
              ? "relative flex min-h-0 flex-col overflow-hidden p-0"
              : ["overflow-x-hidden overflow-y-auto p-6", reserveFabSpace && "pb-24"]
          )}
        >
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}
